import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  BellRingingIcon,
  BroadcastIcon,
  DeviceMobileIcon,
  FlaskIcon,
  GaugeIcon,
  ListIcon,
  MoonIcon,
  SignOutIcon,
  SpeakerHighIcon,
  SunIcon,
  UsersIcon,
  XIcon,
} from '@phosphor-icons/react';
import { api } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { ROLE_LABEL } from '../lib/format.ts';
import { Button, cx } from './ui.tsx';

const NAV = [
  { to: '/', label: 'Übersicht', icon: GaugeIcon, end: true },
  { to: '/alarme', label: 'Alarme', icon: BellRingingIcon, end: false },
  { to: '/regeln', label: 'Weiterleitung', icon: BroadcastIcon, end: false },
  { to: '/quellen', label: 'Quellen', icon: ListIcon, end: false },
  { to: '/kanaele', label: 'Kanäle', icon: DeviceMobileIcon, end: false },
  { to: '/toene', label: 'Töne', icon: SpeakerHighIcon, end: false },
  { to: '/test', label: 'Testbereich', icon: FlaskIcon, end: false },
  { to: '/benutzer', label: 'Benutzer', icon: UsersIcon, end: false },
] as const;

function useTheme() {
  const [theme, setTheme] = useState<'light' | 'dark'>(
    () => (document.documentElement.dataset.theme as 'light' | 'dark') ?? 'dark',
  );

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('meshalert-theme', theme);
  }, [theme]);

  return { theme, toggle: () => setTheme((t) => (t === 'dark' ? 'light' : 'dark')) };
}

export function Layout() {
  const { user, signOut } = useSession();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const [navOpen, setNavOpen] = useState(false);

  // The badge is the reason to look at the sidebar at all, so it stays fresh.
  const { data: stats } = useQuery({
    queryKey: ['stats', 'overview'],
    queryFn: () => api.stats.overview(),
    refetchInterval: 15_000,
  });

  return (
    <div className="flex min-h-[100dvh] bg-base">
      {navOpen ? (
        <button
          type="button"
          aria-label="Navigation schließen"
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
        />
      ) : null}

      <aside
        className={cx(
          'fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r border-line bg-sunken',
          'transition-transform duration-200 md:translate-x-0',
          navOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <span className="flex items-center gap-2">
            <span aria-hidden className="size-2 rounded-full bg-accent" />
            <span className="text-[15px] font-semibold tracking-tight text-ink">MeshAlert</span>
          </span>
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            aria-label="Navigation schließen"
            className="text-ink-3 md:hidden"
          >
            <XIcon size={18} />
          </button>
        </div>

        <nav className="flex-1 space-y-0.5 px-2 py-2">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setNavOpen(false)}
              className={({ isActive }) =>
                cx(
                  'flex h-9 items-center gap-2.5 rounded-md px-2.5 text-[13px] transition-colors',
                  isActive
                    ? 'bg-raised font-medium text-ink shadow-[var(--shadow-card)]'
                    : 'text-ink-2 hover:bg-hover hover:text-ink',
                )
              }
            >
              <Icon size={17} weight="regular" />
              <span className="flex-1">{label}</span>
              {to === '/alarme' && stats && stats.alerts_firing > 0 ? (
                <span className="tnum rounded-sm bg-critical-soft px-1.5 py-0.5 text-[11px] font-semibold text-critical">
                  {stats.alerts_firing}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-line p-2">
          <div className="flex items-center gap-2 rounded-md px-2.5 py-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-ink">{user?.name}</p>
              <p className="truncate text-[11px] text-ink-3">
                {user ? ROLE_LABEL[user.role] : ''}
              </p>
            </div>
            <button
              type="button"
              onClick={toggle}
              aria-label={theme === 'dark' ? 'Helles Design' : 'Dunkles Design'}
              className="rounded-md p-1.5 text-ink-3 transition-colors hover:bg-hover hover:text-ink"
            >
              {theme === 'dark' ? <SunIcon size={16} /> : <MoonIcon size={16} />}
            </button>
            <button
              type="button"
              onClick={async () => {
                await signOut();
                navigate('/anmelden');
              }}
              aria-label="Abmelden"
              className="rounded-md p-1.5 text-ink-3 transition-colors hover:bg-hover hover:text-ink"
            >
              <SignOutIcon size={16} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col md:pl-60">
        <button
          type="button"
          onClick={() => setNavOpen(true)}
          className="sticky top-0 z-20 flex h-12 items-center gap-2 border-b border-line bg-base px-4 text-[13px] text-ink-2 md:hidden"
        >
          <ListIcon size={18} />
          Menü
        </button>

        <main className="mx-auto w-full max-w-[1280px] flex-1 px-4 py-6 md:px-8 md:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-ink">{title}</h1>
        {description ? (
          <p className="mt-1 max-w-[68ch] text-[13px] leading-relaxed text-ink-2">{description}</p>
        ) : null}
      </div>
      {action}
    </header>
  );
}

export { Button };
